import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, TrendingUp, Package, MessageSquare, Database, Zap, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function DocsSignals() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const brandExample = `GET /api/v1/signals/brand?brand_id=brand_xyz&window=30d
X-CI-Api-Key: <your-api-key>
Content-Type: application/json`;

  const brandResponse = `{
  "brand_id": "brand_xyz",
  "time_window": "last_30_days",
  "aggregate": {
    "total_views": 128450,
    "total_purchases": 6425,
    "overall_conversion_rate": 0.05
  },
  "trend": {
    "views_vs_prev_period": "up",
    "views_change_pct": 12.3,
    "purchases_vs_prev_period": "flat",
    "purchases_change_pct": 1.1,
    "conversion_vs_prev_period": "down",
    "conversion_change_pct": -3.7
  },
  "top_products": [
    {
      "product_id": "prod_123",
      "title": "Red Performance Legging",
      "views": 18450,
      "purchases": 1220,
      "conversion_rate": 0.0661,
      "trend": "up"
    }
  ],
  "meta": {
    "confidence": "high",
    "last_updated": "2025-12-22T09:00:00Z"
  }
}`;

  const productExample = `GET /api/v1/signals/product?brand_id=brand_xyz&product_id=prod_123&window=7d
X-CI-Api-Key: <your-api-key>
Content-Type: application/json`;

  const productResponse = `{
  "brand_id": "brand_xyz",
  "product": {
    "product_id": "prod_123",
    "title": "Red Performance Legging"
  },
  "time_window": "last_7_days",
  "activity": {
    "views": 2450,
    "purchases": 185,
    "conversion_rate": 0.0755
  },
  "trend": {
    "views_vs_prev_period": "up",
    "views_change_pct": 18.9,
    "purchases_vs_prev_period": "up",
    "purchases_change_pct": 22.4,
    "conversion_vs_prev_period": "flat",
    "conversion_change_pct": 0.2
  },
  "signal": {
    "activity_tier": "high",
    "comment": "Strong recent activity and stable conversion."
  },
  "meta": {
    "confidence": "high",
    "last_updated": "2025-12-22T10:00:00Z"
  }
}`;

  const contextExample = `POST /api/v1/signals/context
X-CI-Api-Key: <your-api-key>
Content-Type: application/json

{
  "brand_id": "brand_xyz",
  "product_id": "prod_123",
  "country": "US",
  "window": "7d"
}`;

  const contextResponse = `{
  "brand_id": "brand_xyz",
  "product_id": "prod_123",
  "time_window": "last_7_days",
  "headline": "High recent interest and purchases, outperforming brand average.",
  "key_signals": [
    {
      "name": "product_activity",
      "value": "high",
      "details": {
        "views": 2450,
        "purchases": 185,
        "conversion_rate": 0.0755
      }
    },
    {
      "name": "product_trend",
      "value": "up",
      "details": {
        "views_change_pct": 18.9,
        "purchases_change_pct": 22.4
      }
    },
    {
      "name": "brand_background",
      "value": "up",
      "details": {
        "brand_total_views_change_pct": 12.3,
        "brand_total_purchases_change_pct": 8.9
      }
    }
  ],
  "recommendations": [
    "Consider allocating more traffic to this product: it is outperforming your brand average.",
    "If demand remains strong over the next 1-2 weeks, you may want to test a small price increase."
  ],
  "meta": {
    "confidence": "medium",
    "derived_from": ["v1/signals/brand", "v1/signals/product"],
    "last_updated": "2025-12-22T10:05:00Z"
  }
}`;

  const endpoints = [
    {
      method: 'GET',
      path: '/api/v1/signals/brand',
      icon: TrendingUp,
      title: 'Brand Signals',
      description: 'Aggregated view of brand-level activity: views, purchases, conversion, trends, and top products.',
      useCases: [
        'Brand activity overview in your internal dashboards',
        'Monitoring "are we trending up or down" at a glance',
        'Feeding pricing / growth / operations decisions with context'
      ],
      params: [
        { name: 'brand_id', type: 'string', required: true, desc: 'Your CI-assigned brand identifier' },
        { name: 'window', type: 'string', required: false, desc: 'Time window: 1d, 7d, 30d, or 90d (default: 30d)' }
      ],
      request: brandExample,
      response: brandResponse
    },
    {
      method: 'GET',
      path: '/api/v1/signals/product',
      icon: Package,
      title: 'Product Signals',
      description: 'Activity for a single product: views & purchases, conversion rate, trend vs previous period, and activity tier (high/medium/low) + comment.',
      useCases: [
        'PDP-level decisioning ("is this product hot or cold?")',
        'Merchandising dashboards (finding top movers and underperformers)',
        'Powering UI labels (e.g., "Popular right now") in your own frontend'
      ],
      params: [
        { name: 'brand_id', type: 'string', required: true, desc: 'Your CI-assigned brand identifier' },
        { name: 'product_id', type: 'string', required: true, desc: 'Your product identifier' },
        { name: 'window', type: 'string', required: false, desc: 'Time window: 1d, 7d, 30d, or 90d (default: 7d)' }
      ],
      request: productExample,
      response: productResponse
    },
    {
      method: 'POST',
      path: '/api/v1/signals/context',
      icon: MessageSquare,
      title: 'Context Signals',
      description: 'A compact, opinionated summary with a headline, key signals, and optional recommendations. This is the "noise-clearing" API.',
      useCases: [
        'In a "Context" sidebar in your CI dashboard',
        'For agents / assistants that need a quick summary before taking action',
        'For internal tools (e.g., a Slack bot that replies with this payload)'
      ],
      params: [
        { name: 'brand_id', type: 'string', required: true, desc: 'Your brand identifier (in body)' },
        { name: 'product_id', type: 'string', required: false, desc: 'Optional product ID for product-specific context' },
        { name: 'country', type: 'string', required: false, desc: 'Country code (default: US)' },
        { name: 'window', type: 'string', required: false, desc: 'Time window (default: 7d)' }
      ],
      request: contextExample,
      response: contextResponse
    }
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          Signals API
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Commerce Index Signals
        </h1>
        <p className="text-lg text-[#94A3B8] mb-2">
          API-first access to brand, product, and market signals derived from Commerce Index events.
        </p>
        <p className="text-[#64748B]">
          Subtitle: Structured, aggregated signals derived from the events you send to Commerce Index.
        </p>
      </div>

      {/* What Are CI Signals? */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">What Are CI Signals?</h2>
        <div className="p-6 bg-[#0D1628] rounded-lg border border-[#22304A] space-y-4">
          <p className="text-[#94A3B8]">
            Commerce Index Signals (CI Signals) are <strong className="text-white">structured, aggregated signals</strong> derived 
            from the events you send to Commerce Index (views, purchases, and later more), as well as cross-brand data where available.
          </p>
          <p className="text-[#94A3B8]">
            Instead of pulling raw events or building your own aggregation pipelines, you can use CI Signals to answer questions like:
          </p>
          <ul className="space-y-2 pl-4">
            {[
              '"How is my brand performing over the last 30 days?"',
              '"Is this product hot, steady, or cold right now?"',
              '"What are the key context signals I should know about this SKU or brand before I make a decision?"'
            ].map((q, i) => (
              <li key={i} className="flex items-start gap-3 text-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3EF1C7] mt-2 flex-shrink-0" />
                <span className="italic">{q}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-4 border-t border-[#22304A]">
            <p className="text-sm text-[#94A3B8] mb-3">CI Signals is designed to be:</p>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: Database, label: 'API-first', desc: 'Simple REST endpoints, JSON responses' },
                { icon: Zap, label: 'Opinionated', desc: 'Includes trends and tiers (up/flat/down, high/medium/low)' },
                { icon: Code2, label: 'Composable', desc: 'Easy to use in dashboards, BI, agents, internal tools' }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-[#3EF1C7] mt-0.5" />
                    <div>
                      <span className="text-white font-medium text-sm">{item.label}</span>
                      <p className="text-[#64748B] text-xs">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[#94A3B8] text-sm mt-4">
            At launch, CI Signals focuses on <strong className="text-white">brand and product activity</strong> using the events 
            you already send via CI Proof.
          </p>
        </div>
      </section>

      {/* Available Signal Endpoints */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">Available Signal Endpoints (v0)</h2>
        <p className="text-[#94A3B8] mb-6">
          CI Signals v0 exposes three main endpoints. These can be used independently or together, 
          depending on what you are building.
        </p>
        
        <div className="grid gap-4">
          {endpoints.map((ep, i) => {
            const Icon = ep.icon;
            return (
              <div key={i} className="p-4 bg-[#1E293B] rounded-lg border border-[#334155] flex items-start gap-4">
                <Icon className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{ep.title}</span>
                    <code className="text-xs text-[#64748B] bg-[#0B1120] px-2 py-0.5 rounded">{ep.path}</code>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{ep.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Authentication */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">Authentication</h2>
        <div className="p-6 bg-[#0D1628] rounded-lg border border-[#22304A]">
          <p className="text-[#94A3B8] mb-4">All Signals endpoints require an API key:</p>
          <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 text-sm text-[#E2E8F0] overflow-x-auto">
            <code>{`X-CI-Api-Key: <your-api-key>
Content-Type: application/json`}</code>
          </pre>
          <p className="text-[#64748B] text-sm mt-4">
            You will receive an API key during onboarding. Treat it like any other secret.
          </p>
        </div>
      </section>

      {/* Detailed Endpoints */}
      <section className="space-y-8">
        <h2 className="text-xl font-semibold text-white mb-4">API Reference</h2>
        
        {endpoints.map((endpoint, index) => {
          const Icon = endpoint.icon;
          return (
            <Card key={index} className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-5 h-5 text-[#3EF1C7]" />
                  <Badge className={endpoint.method === 'GET' 
                    ? 'bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30'
                    : 'bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30'
                  }>
                    {endpoint.method}
                  </Badge>
                  <code className="text-[#E2E8F0] font-mono">{endpoint.path}</code>
                </div>
                <CardTitle className="text-white text-lg">{endpoint.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#94A3B8]">{endpoint.description}</p>
                
                {/* Use Cases */}
                <div className="bg-[#1E293B] rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2 text-sm">Typical Uses:</h4>
                  <ul className="space-y-1">
                    {endpoint.useCases.map((uc, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                        <span className="text-[#3EF1C7]">•</span>
                        {uc}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Parameters */}
                <div>
                  <h4 className="text-white font-medium mb-3">Parameters</h4>
                  <div className="space-y-2">
                    {endpoint.params.map((param, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-3 py-2 border-b border-[#22304A] last:border-0">
                        <code className="text-[#3EF1C7] text-sm bg-[#3EF1C7]/10 px-2 py-0.5 rounded">
                          {param.name}
                        </code>
                        <span className="text-[#7A5CFF] text-sm">{param.type}</span>
                        {param.required && (
                          <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                            Required
                          </Badge>
                        )}
                        <span className="text-[#94A3B8] text-sm flex-1">{param.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Request/Response Tabs */}
                <Tabs defaultValue="request" className="w-full">
                  <TabsList className="bg-[#1E293B] border border-[#334155]">
                    <TabsTrigger value="request" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                      Request
                    </TabsTrigger>
                    <TabsTrigger value="response" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                      Response
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="request">
                    <div className="relative">
                      <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                        <code>{endpoint.request}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                        onClick={() => copyToClipboard(endpoint.request, `req-${index}`)}
                      >
                        {copiedCode === `req-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="response">
                    <div className="relative">
                      <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0] max-h-96">
                        <code>{endpoint.response}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                        onClick={() => copyToClipboard(endpoint.response, `res-${index}`)}
                      >
                        {copiedCode === `res-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Signal Values Reference */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">Signal Values Reference</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white text-lg">Trend Direction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { value: 'up', desc: 'Metric increased >5% vs previous period', color: '#3EF1C7' },
                  { value: 'flat', desc: 'Metric changed <5% vs previous period', color: '#94A3B8' },
                  { value: 'down', desc: 'Metric decreased >5% vs previous period', color: '#FFD166' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <code className="text-sm px-2 py-1 rounded font-medium" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                      {item.value}
                    </code>
                    <span className="text-[#94A3B8] text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white text-lg">Activity Tier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { value: 'high', desc: 'Above average activity for this brand', color: '#3EF1C7' },
                  { value: 'medium', desc: 'Normal activity levels', color: '#7A5CFF' },
                  { value: 'low', desc: 'Below average activity', color: '#FFD166' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <code className="text-sm px-2 py-1 rounded font-medium" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                      {item.value}
                    </code>
                    <span className="text-[#94A3B8] text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How Signals Are Computed */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">How Signals Are Computed</h2>
        <div className="p-6 bg-[#0D1628] rounded-lg border border-[#22304A] space-y-4">
          <p className="text-[#94A3B8]">
            Signals are derived from the view and purchase events you send via:
          </p>
          <ul className="space-y-2 pl-4">
            <li className="flex items-start gap-2 text-[#E2E8F0] text-sm">
              <span className="text-[#3EF1C7]">•</span>
              The CI JavaScript snippet (Proof integration)
            </li>
            <li className="flex items-start gap-2 text-[#E2E8F0] text-sm">
              <span className="text-[#3EF1C7]">•</span>
              Or server-side <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-1 rounded">/api/collect/product-view</code> and <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-1 rounded">/api/collect/product-purchase</code> endpoints
            </li>
          </ul>
          <p className="text-[#94A3B8] mt-4">
            We aggregate events over the requested window (e.g., last 7 days), then compare them to a 
            previous window to compute <strong className="text-white">trend (up/flat/down)</strong>.
          </p>
          <p className="text-[#94A3B8]">
            We compute <strong className="text-white">confidence</strong> based on:
          </p>
          <ul className="space-y-1 pl-4">
            <li className="flex items-start gap-2 text-[#94A3B8] text-sm">
              <span className="text-[#3EF1C7]">•</span>
              Volume of data in that window
            </li>
            <li className="flex items-start gap-2 text-[#94A3B8] text-sm">
              <span className="text-[#3EF1C7]">•</span>
              Consistency of data over time
            </li>
          </ul>
          <p className="text-[#64748B] text-sm mt-4">
            As CI expands and more brands integrate, CI Signals will also incorporate category and market-level context.
          </p>
        </div>
      </section>

      {/* Next Steps */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">Next Steps</h2>
        <div className="p-6 bg-[#1E293B] rounded-lg border border-[#334155]">
          <p className="text-[#94A3B8] mb-4">To start using CI Signals, make sure you have:</p>
          <ol className="space-y-2 list-decimal list-inside text-[#E2E8F0] text-sm mb-6">
            <li>Integrated CI Proof (view/purchase events), or</li>
            <li>Arranged a data integration with the Commerce Index team</li>
          </ol>
          <p className="text-[#94A3B8] mb-4">Then:</p>
          <ol className="space-y-2 list-decimal list-inside text-[#E2E8F0] text-sm mb-6">
            <li>Get your API key</li>
            <li>Call your first Signals endpoint</li>
          </ol>
          <p className="text-[#94A3B8] mb-4">You can find full API references under:</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/docs/examples" className="text-[#3EF1C7] hover:underline text-sm">Examples & Recipes →</Link>
            <Link to="/docs/rest-api" className="text-[#3EF1C7] hover:underline text-sm">REST API →</Link>
            <Link to="/docs/faq" className="text-[#3EF1C7] hover:underline text-sm">FAQ →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
